import { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay, subMonths, addMonths, parseISO } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Bell, Plus, Search } from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '../contexts/AuthContext';
import { supabase, Entry, Notification } from '../lib/supabase';
import EntryModal from '../components/EntryModal';
import DayDetailModal from '../components/DayDetailModal';
import NotificationsDropdown from '../components/NotificationsDropdown';
import SearchModal from '../components/SearchModal';
import toast from 'react-hot-toast';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
}

export default function Calendar() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [modalKey, setModalKey] = useState<string | null>(null);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [dayDetailOpen, setDayDetailOpen] = useState(false);
  const [dayDetailDate, setDayDetailDate] = useState<Date | null>(null);
  const [dayDetailEntries, setDayDetailEntries] = useState<Entry[]>([]);
  const [preselectedEntry, setPreselectedEntry] = useState<Entry | undefined>(undefined);

  const firstDayOfMonth = startOfMonth(currentDate);
  const lastDayOfMonth = endOfMonth(currentDate);
  const days = eachDayOfInterval({ start: firstDayOfMonth, end: lastDayOfMonth });

  useEffect(() => {
    const handleRefresh = () => {
      fetchEntries();
    };

    window.addEventListener('refresh-calendar', handleRefresh);
    return () => window.removeEventListener('refresh-calendar', handleRefresh);
  }, []);

  useEffect(() => {
    fetchEntries();
    fetchNotifications();
  }, [currentDate]);

  useEffect(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      const parsed = parseISO(dateParam);
      if (!isNaN(parsed.getTime())) {
        setCurrentDate(parsed);
        setSelectedDate(parsed);
        setModalKey(`search-nav-${dateParam}`);
        setIsModalOpen(true);
        searchParams.delete('date');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, setSearchParams]);

  async function fetchEntries() {
    try {
      const { data, error } = await supabase
        .from('entries')
        .select(`
          *,
          farmer:farmers(id, name),
          customer:customers(id, name),
          products(
            id,
            description,
            crates,
            pallets
          )
        `)
        .gte('date', format(firstDayOfMonth, 'yyyy-MM-dd'))
        .lte('date', format(lastDayOfMonth, 'yyyy-MM-dd'))
        .order('date', { ascending: true });

      if (error) throw error;
      setEntries(data || []);
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Failed to load entries');
    }
  }

  async function fetchNotifications() {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user?.id)
        .eq('read', false)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotifications(data || []);
      setUnreadCount(data?.length || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }

  function handleDateClick(date: Date) {
    setSelectedDate(date);
    const dateEntries = entries.filter((entry) => isSameDay(new Date(entry.date), date));

    if (isMobile && dateEntries.length > 0) {
      setDayDetailDate(date);
      setDayDetailEntries(dateEntries);
      setDayDetailOpen(true);
    } else {
      setModalKey(date.toISOString());
      setIsModalOpen(true);
    }
  }

  function handleNewEntry() {
    setSelectedDate(new Date());
    setModalKey(`new-entry-${Date.now()}`);
    setIsModalOpen(true);
  }

  function handleModalClose() {
    setIsModalOpen(false);
    setSelectedDate(null);
    setModalKey(null);
    setPreselectedEntry(undefined);
  }

  function handleDayDetailClose() {
    setDayDetailOpen(false);
    setDayDetailDate(null);
    setDayDetailEntries([]);
  }

  function handleDayDetailAddEntry() {
    handleDayDetailClose();
    setModalKey(`new-entry-${Date.now()}`);
    setIsModalOpen(true);
  }

  function handleDayDetailEditEntry(entry: Entry) {
    handleDayDetailClose();
    setPreselectedEntry(entry);
    setModalKey(`edit-entry-${entry.id}`);
    setIsModalOpen(true);
  }

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 mb-6 sm:mb-8">
        <div className="flex items-center justify-between sm:justify-start sm:space-x-4">
          <button
            onClick={() => setCurrentDate(subMonths(currentDate, 1))}
            className="p-2 rounded-full hover:bg-white"
          >
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          </button>
          <h2 className="text-xl font-semibold text-gray-900">
            {format(currentDate, 'MMMM yyyy')}
          </h2>
          <button
            onClick={() => setCurrentDate(addMonths(currentDate, 1))}
            className="p-2 rounded-full hover:bg-white"
          >
            <ChevronRight className="h-5 w-5 text-gray-600" />
          </button>
        </div>
        <div className="flex items-center justify-end space-x-2 sm:space-x-4">
          <button
            onClick={() => setIsSearchOpen(true)}
            className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Search className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Search Entries</span>
          </button>
          <button
            onClick={handleNewEntry}
            className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Plus className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">New Entry</span>
          </button>
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="p-2 rounded-full hover:bg-gray-100 relative"
            >
              <Bell className="h-6 w-6 text-gray-600" />
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>
            {showNotifications && (
              <NotificationsDropdown
                notifications={notifications}
                onClose={() => setShowNotifications(false)}
                onMarkAsRead={fetchNotifications}
              />
            )}
          </div>
        </div>
      </div>

        <div className="bg-white rounded-lg shadow overflow-hidden calendar-grid">
        <div className="grid grid-cols-7 gap-px bg-gray-200 text-center">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div
              key={day}
              className="bg-gray-50 py-2 text-xs sm:text-sm font-semibold text-gray-700"
            >
              <span className="sm:hidden">{day.charAt(0)}</span>
              <span className="hidden sm:inline">{day}</span>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-px bg-gray-200">
          {Array.from({ length: firstDayOfMonth.getDay() }).map((_, index) => (
            <div key={`empty-${index}`} className="bg-white min-h-[80px] sm:min-h-[120px]" />
          ))}
          {days.map((day) => {
            const dayEntries = entries.filter(entry => isSameDay(new Date(entry.date), day));
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isSelected = selectedDate && isSameDay(day, selectedDate);

            return (
              <div
                key={day.toISOString()}
                onClick={() => handleDateClick(day)}
                className={clsx(
                  'relative bg-white p-1 sm:p-2 hover:bg-gray-50 focus:z-10 min-h-[80px] sm:min-h-[120px] flex flex-col cursor-pointer',
                  {
                    'text-gray-400': !isCurrentMonth,
                    'text-gray-900': isCurrentMonth,
                    'bg-indigo-50': isSelected,
                  }
                )}
              >
                <time
                  dateTime={format(day, 'yyyy-MM-dd')}
                  className={clsx(
                    'ml-auto flex h-6 w-6 items-center justify-center rounded-full text-sm',
                    {
                      'bg-indigo-600 text-white': isToday(day),
                      'font-semibold': isCurrentMonth,
                    }
                  )}
                >
                  {format(day, 'd')}
                </time>
                {dayEntries.length > 0 && (
                  <div className="mt-auto space-y-1">
                    {/* Desktop: full chips */}
                    <div className="hidden sm:block">
                      {dayEntries.slice(0, 3).map((entry) => (
                        <div
                          key={entry.id}
                          className={clsx(
                            'px-1.5 py-1 text-xs rounded hover:bg-opacity-75',
                            entry.invoiced
                              ? 'bg-green-100 text-green-700'
                              : 'bg-indigo-100 text-indigo-700'
                          )}
                        >
                          <div className="line-clamp-1">
                            {entry.delivery_note_number || 'No DN'}
                          </div>
                        </div>
                      ))}
                      {dayEntries.length > 3 && (
                        <div className="px-1.5 py-1 text-xs text-gray-500">
                          +{dayEntries.length - 3} more
                        </div>
                      )}
                    </div>
                    {/* Mobile: compact entry info */}
                    <div className="sm:hidden mt-auto flex flex-col items-center gap-0.5">
                      {dayEntries.length === 1 ? (
                        <div className={clsx(
                          'w-full px-1 py-0.5 text-[9px] rounded text-center font-medium truncate leading-tight',
                          dayEntries[0].invoiced ? 'bg-green-100 text-green-700' : 'bg-indigo-100 text-indigo-700'
                        )}>
                          {dayEntries[0].delivery_note_number || 'No DN'}
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-center gap-1">
                            {dayEntries.slice(0, 3).map((entry) => (
                              <span
                                key={entry.id}
                                className={clsx(
                                  'block w-2 h-2 rounded-full',
                                  entry.invoiced ? 'bg-green-500' : 'bg-indigo-500'
                                )}
                              />
                            ))}
                          </div>
                          <span className={clsx(
                            'text-[9px] font-medium leading-none',
                            dayEntries.every(e => e.invoiced) ? 'text-green-600' : 'text-indigo-600'
                          )}>
                            {dayEntries.length}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Day Detail Modal */}
      {dayDetailOpen && dayDetailDate && (
        <DayDetailModal
          isOpen={dayDetailOpen}
          onClose={handleDayDetailClose}
          date={dayDetailDate}
          entries={dayDetailEntries}
          onAddEntry={handleDayDetailAddEntry}
          onEditEntry={handleDayDetailEditEntry}
        />
      )}

      {/* Entry Modal */}
      {isModalOpen && selectedDate && modalKey && (
        <EntryModal
          key={modalKey}
          isOpen={isModalOpen}
          onClose={handleModalClose}
          date={selectedDate}
          entries={entries.filter(entry => isSameDay(new Date(entry.date), selectedDate))}
          onEntrySaved={() => {
            fetchEntries();
          }}
          preselectedEntry={preselectedEntry}
        />
      )}

      {/* Search Modal */}
      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        onSelectDate={(date) => {
          setCurrentDate(date);
          handleDateClick(date);
          setIsSearchOpen(false);
        }}
      />
    </div>
  );
}